import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';

/**
 * Configuration for MSAL Authentication
 */

// Check if the Web Crypto API is available and working
// This is a more comprehensive check that also verifies if we're in a secure context
// which is required for the Web Crypto API to work properly in some browsers
const isWebCryptoAvailable = (function() {
  // Check if window is defined (for SSR)
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if we're in a secure context (required for Web Crypto API in some browsers)
  const isSecureContext = window.isSecureContext === true;

  // Check if crypto is available
  const hasCrypto = typeof window.crypto !== 'undefined';

  // Check if subtle is available
  const hasSubtle = hasCrypto && typeof window.crypto.subtle !== 'undefined';

  // Check if digest function is available
  const hasDigest = hasSubtle && typeof window.crypto.subtle.digest === 'function';

  // Check if getRandomValues function is available (also needed for PKCE)
  const hasRandomValues = hasCrypto && typeof window.crypto.getRandomValues === 'function';

  // Log detailed information for debugging
  console.log('[MSAL Config] Web Crypto API availability check:', {
    isSecureContext,
    hasCrypto,
    hasSubtle,
    hasDigest,
    hasRandomValues,
    protocol: window.location.protocol,
    host: window.location.host,
    userAgent: navigator.userAgent
  });

  // Return true only if all required features are available
  return isSecureContext && hasCrypto && hasSubtle && hasDigest && hasRandomValues;
})();

// MSAL configuration
export const msalConfig = {
  auth: {
    clientId: '2a0024ea-7551-4154-b6da-ad27e128ef2d', // Client ID (Secret ID)
    authority: 'https://login.microsoftonline.com/common', // Using common endpoint for multi-tenant support
    redirectUri: window.location.origin, // SPA redirect URI - not using callback endpoint
    // Disable PKCE if Web Crypto API is not available
    // This is a workaround for browsers that don't support the Web Crypto API
    // Note: This reduces security but allows authentication to work in unsupported browsers
    usePkce: isWebCryptoAvailable,
    // Explicitly set the application type to SPA to allow cross-origin token redemption
    clientType: 'spa',
    // Allow redirects within an iframe to help with CORS issues
    allowRedirectInIframe: true,
    // Don't navigate to the original request URL after login
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true, // Store auth state in cookies to help with CORS issues
  },
  system: {
    // Set a longer popup timeout (default is 60000ms or 1 minute)
    // This gives users more time to interact with the Microsoft login popup
    popupWindowTimeout: 300000, // 5 minutes in milliseconds

    // Enhanced popup window attributes for better compatibility
    popupWindowAttributes: {
      width: 600,
      height: 700,
      top: Math.max(0, (window.screen.height - 700) / 2),
      left: Math.max(0, (window.screen.width - 600) / 2),
      scrollbars: 'yes',
      resizable: 'yes',
      status: 'no',
      toolbar: 'no',
      menubar: 'no',
      location: 'no'
    },

    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }



        switch (level) {
          case LogLevel.Error:
            console.error(`[MSAL Error] ${message}`);
            return;
          case LogLevel.Info:
            console.info(`[MSAL Info] ${message}`);
            return;
          case LogLevel.Verbose:
            console.debug(`[MSAL Verbose] ${message}`);
            return;
          case LogLevel.Warning:
            console.warn(`[MSAL Warning] ${message}`);
            return;
          default:
            return;
        }
      },
      piiLoggingEnabled: false,
      logLevel: LogLevel.Info,
    },
    // Specify which URLs should be treated as same-origin for CORS purposes
    corsRelativeUrls: {
      // Add Microsoft's token endpoint to the list of relative URLs
      'https://login.microsoftonline.com/common/oauth2/v2.0/token': true
    },
  },
};

// MSAL Guard configuration
export const msalGuardConfig: MsalGuardConfiguration = {
  interactionType: InteractionType.Popup,
  authRequest: {
    scopes: ['user.read'],
  },
  loginFailedRoute: '/login-failed',
};

// MSAL Interceptor configuration
export const msalInterceptorConfig: MsalInterceptorConfiguration = {
  interactionType: InteractionType.Popup,
  protectedResourceMap: new Map([
    ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    // Add other API endpoints and their required scopes here
  ]),
};

// MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);
